import { useState, useEffect } from "react";
import { useRequest } from "ahooks";
import { Table, Space, Input, Select, DatePicker } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  announcement as announcementService,
  setting as settingService,
} from "@/services";
import { useAuthContext } from "@/components/auth/AuthProvider";
import AnnouncementPublishButton from "./AnnouncementPublishButton";
import AnnouncementEditButton from "./AnnouncementEditButton";
import AnnouncementDeleteButton from "./AnnouncementDeleteButton";
import AnnouncementRecallButton from "./AnnouncementRecallButton";

import styles from "./index.module.less";

function DraftAnnouncement() {
  const [version, setVersion] = useState(Date.now());
  const [column, setColumn] = useState();
  const [type, setType] = useState();
  const [publication, setPublication] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [keyword, setKeyword] = useState();
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const auth = useAuthContext();
  const { data: publications = { results: [] }, loading: publicationLoading } =
    useRequest(() => settingService.listPublication({}, auth.token), {
      ready: !!auth.token,
      refreshDeps: [auth.token],
    });
  const {
    data = { total: 0, results: [] },
    loading,
    run,
  } = useRequest(
    (params, token) => {
      return announcementService.listMyDraftAnnouncement(params, token);
    },
    {
      manual: true,
    }
  );

  useEffect(() => {
    if (auth) {
      run(
        {
          version,
          column,
          type,
          publication,
          start_date: startDate,
          end_date: endDate,
          search: keyword,
          page,
          page_size: pageSize,
        },
        auth.token
      );
    }
  }, [
    run,
    auth,
    keyword,
    page,
    pageSize,
    version,
    type,
    column,
    publication,
    startDate,
    endDate,
  ]);

  const columns = [
    {
      title: "序号",
      key: "num",
      width: 50,
      render: (text, record, index) => `${index + 1}`,
    },
    {
      title: "栏目",
      width: 90,
      dataIndex: "column_name",
    },
    {
      title: "类型",
      width: 90,
      key: "type_name",
      render: (record) => {
        if (record.column === 0) {
          return record.type_name;
        } else return "-";
      },
    },
    {
      title: "刊物",
      key: "publication",
      width: 120,
      render: (record) => {
        if (record.column === 1) {
          return record.publication?.name;
        } else return "-";
      },
    },
    {
      title: "接收单位",
      key: "type",
      width: 150,
      render: (record) =>
        record.relation_label ||
        record.records.map((r) => r.department.name).join(", "),
    },
    {
      title: "标题",
      dataIndex: "title",
      render: (text, record) => (
        <Link to={`/workshop/announce/detail/${record.id}`}>{text}</Link>
      ),
    },
    {
      title: "日期",
      dataIndex: "published_at",
      width: 120,
      render: (text, record) => moment(text).format("YYYY-MM-DD"),
    },
    {
      title: "状态",
      dataIndex: "status_name",
    },
    {
      title: "操作",
      key: "operation",
      align: "right",
      render: (record) =>
        record.status === 0 ? (
          <Space size="small">
            <AnnouncementPublishButton
              messageId={record.id}
              callback={setVersion}
              column_name={record.column_name}
              relation_label={record.relation_label}
              title={record.title}
            />
            <AnnouncementEditButton messageId={record.id} />
            <AnnouncementDeleteButton
              messageId={record.id}
              callback={setVersion}
            />
          </Space>
        ) : (
          <Space>
            <AnnouncementRecallButton
              messageId={record.id}
              callback={setVersion}
            />
            <AnnouncementDeleteButton
              messageId={record.id}
              callback={setVersion}
            />
          </Space>
        ),
    },
  ];
  return (
    <div className={styles.draftAnnouncement}>
      <div className="table-toolbar">
        <div className="left"></div>
        <div className="right">
          <Space>
            <div>
              栏目：
              <Select
                style={{ width: 120 }}
                onChange={(value) => setColumn(value)}
                allowClear
              >
                <Select.Option value="0">公告</Select.Option>
                <Select.Option value="1">成刊</Select.Option>
                <Select.Option value="2">交流学习</Select.Option>
              </Select>
            </div>
            {column === "0" && (
              <div>
                类型：
                <Select
                  style={{ width: 120 }}
                  onChange={(value) => setType(value)}
                  allowClear
                >
                  <Select.Option value="0">工作通知</Select.Option>
                  <Select.Option value="1">约稿通知</Select.Option>
                </Select>
              </div>
            )}

            {column === "1" && (
              <div>
                刊物：
                <Select
                  loading={publicationLoading}
                  style={{ width: 120 }}
                  onChange={(value) => setPublication(value)}
                  allowClear
                >
                  {publications.results.map((p) => (
                    <Select.Option key={`publication-${p.id}`} value={p.id}>
                      {p.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
            <div>
              日期：
              <DatePicker.RangePicker
                disabledDate={(current) => current.isAfter(moment())}
                onChange={(value) => {
                  setStartDate(value[0].format("YYYY-MM-DD"));
                  setEndDate(value[1].format("YYYY-MM-DD"));
                }}
              />
            </div>
            <div>
              标题：
              <Input
                prefix={<SearchOutlined style={{ color: "#ccc" }} />}
                style={{ width: 220 }}
                placeholder="搜索内容"
                allowClear
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </Space>
        </div>
      </div>
      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={data.results}
        loading={loading}
        pagination={{
          pageSize: pageSize,
          total: data.count,
        }}
        onChange={({ current, pageSize }) => {
          setPage(current);
          setPageSize(pageSize);
        }}
      />
    </div>
  );
}

export default DraftAnnouncement;
